import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  Archive,
  CheckCircle2,
  Clock,
  Cloud,
  Copy,
  Database,
  HardDriveDownload,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Terminal,
  XCircle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import { RestoreVerifications } from '@/components/database/RestoreVerifications';
import type {
  AssistedBackupCommandResponse,
  BackupStatusResponse,
  PgBackrestBackup,
  PgBackrestBackupType,
  R2RepoObject,
} from '@easyflow/shared';

const TYPE_LABEL: Record<PgBackrestBackupType, string> = {
  full: 'Full',
  diff: 'Differenziale',
  incr: 'Incrementale',
};

const TYPE_ICON: Record<PgBackrestBackupType, typeof Database> = {
  full: Archive,
  diff: HardDriveDownload,
  incr: Database,
};

const TYPE_BADGE: Record<PgBackrestBackupType, string> = {
  full: 'bg-violet-500/15 text-violet-700 dark:text-violet-300',
  diff: 'bg-sky-500/15 text-sky-700 dark:text-sky-300',
  incr: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
};

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

function formatAge(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return 'mai';
  if (seconds < 60) return `${Math.floor(seconds)}s fa`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m fa`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h fa`;
  return `${Math.floor(seconds / 86400)}g fa`;
}

function SlaCard({
  title,
  icon: Icon,
  ageSeconds,
  ok,
  slaTargetSeconds,
}: {
  title: string;
  icon: typeof Database;
  ageSeconds: number | null;
  ok: boolean;
  slaTargetSeconds?: number;
}) {
  const okCls = ok ? 'border-emerald-300 bg-emerald-500/5' : 'border-rose-300 bg-rose-500/5';
  const okText = ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300';
  return (
    <Card className={okCls}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm font-medium">
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            {title}
          </span>
          {ok ? <CheckCircle2 className={`h-4 w-4 ${okText}`} /> : <XCircle className={`h-4 w-4 ${okText}`} />}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1">
        <p className={`text-2xl font-bold ${okText}`}>{formatAge(ageSeconds)}</p>
        {slaTargetSeconds !== undefined && (
          <p className="text-xs text-muted-foreground">
            SLA: &lt; {Math.round((slaTargetSeconds / 3600) * 10) / 10}h
          </p>
        )}
      </CardContent>
    </Card>
  );
}

interface TriggerDialogState {
  open: boolean;
  type: PgBackrestBackupType | null;
  command: AssistedBackupCommandResponse | null;
  busy: boolean;
}

const INITIAL_TRIGGER: TriggerDialogState = { open: false, type: null, command: null, busy: false };

export function BackupTab({ restoreFocusRequest = 0 }: { restoreFocusRequest?: number }) {
  const { toast } = useToast();
  const [data, setData] = useState<BackupStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState<TriggerDialogState>(INITIAL_TRIGGER);
  const handledRestoreFocus = useRef(0);
  useEffect(() => {
    if (!data || restoreFocusRequest === 0 || handledRestoreFocus.current === restoreFocusRequest) return;
    const section = document.getElementById('restore-verifications');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      handledRestoreFocus.current = restoreFocusRequest;
    }
  }, [data, restoreFocusRequest]);

  const fetchData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await api.get<BackupStatusResponse>('/master/database/backups');
      if (res.data) setData(res.data);
      else setError('Risposta vuota dal server');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore di rete');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchData();
    const interval = setInterval(() => { void fetchData(); }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const requestCommand = async (type: PgBackrestBackupType) => {
    setTrigger({ open: true, type, command: null, busy: true });
    try {
      const res = await api.post<AssistedBackupCommandResponse>('/master/database/backup-trigger', { type });
      if (res.data) {
        setTrigger({ open: true, type, command: res.data, busy: false });
      } else {
        toast({ title: 'Errore', description: 'Risposta vuota dal server', variant: 'destructive' });
        setTrigger(INITIAL_TRIGGER);
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      toast({
        title: 'Errore',
        description: e.response?.data?.error?.message ?? e.message ?? 'Errore di rete',
        variant: 'destructive',
      });
      setTrigger(INITIAL_TRIGGER);
    }
  };

  const copyCommand = async () => {
    if (!trigger.command) return;
    try {
      await navigator.clipboard.writeText(trigger.command.command);
      toast({ title: 'Copiato', description: 'Comando copiato negli appunti' });
    } catch {
      toast({ title: 'Errore', description: 'Impossibile copiare negli appunti', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <Card className="border-rose-200 bg-rose-50 dark:bg-rose-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="h-5 w-5" />
            Errore lettura stato backup
          </CardTitle>
          <CardDescription className="text-rose-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => void fetchData()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Riprova
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // Inventario ordinato dal più recente al più vecchio (youngest-first).
  const backups = [...(data.stanza?.backups ?? [])].sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime(),
  );
  const collectedMs = data.collected_at ? Date.parse(data.collected_at) : NaN;
  const bridgeCurrent = data.bridge_healthy && Number.isFinite(collectedMs)
    && Date.now() >= collectedMs && Date.now() - collectedMs <= 45 * 60_000;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Cloud className="h-5 w-5 text-purple-500" />
            DR &amp; Backup — pgBackRest
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Stanza{' '}
            <span className="font-mono">{data.stanza?.name ?? '—'}</span>
            {' · '}repo Cloudflare R2
            {data.collected_at
              ? <> · snapshot {formatAge(data.snapshot_age_seconds)} ({data.node_name ?? '—'})</>
              : <> · nessuno snapshot dal collector DB-1</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void fetchData()} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={() => void requestCommand('incr')}>
            <Terminal className="h-4 w-4 mr-2" />
            Comando backup
          </Button>
        </div>
      </div>

      {/* Bridge down banner */}
      {!bridgeCurrent && (
        <Card className="border-amber-300 bg-amber-500/5">
          <CardContent className="flex items-center gap-2 py-3 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Ponte dati degradato: lo snapshot del collector DB-1 è assente o stantio. I dati mostrati potrebbero non riflettere lo stato reale.
          </CardContent>
        </Card>
      )}

      {/* Logical dump degraded banner */}
      {data.logical_dump?.enabled && !data.logical_dump.healthy && (
        <Card className="border-amber-300 bg-amber-500/5">
          <CardContent className="flex items-center gap-2 py-3 text-sm text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Dump logico complementare degradato
            {data.logical_dump.last_run_age_seconds !== null && <> (ultimo {formatAge(data.logical_dump.last_run_age_seconds)})</>}
            {data.logical_dump.last_error && <>: {data.logical_dump.last_error}</>}
          </CardContent>
        </Card>
      )}

      <RestoreVerifications
        summary={data.restore_gate?.verifications}
        bridgeHealthy={bridgeCurrent}
      />

      {/* Stanza status */}
      {data.stanza && data.stanza.status_code !== 0 && (
        <Card className="border-rose-300 bg-rose-500/5">
          <CardContent className="flex items-center gap-2 py-3 text-sm text-rose-700 dark:text-rose-300">
            <ShieldAlert className="h-4 w-4 shrink-0" />
            Stanza {data.stanza.name}: {data.stanza.status_message} (code {data.stanza.status_code})
          </CardContent>
        </Card>
      )}

      {/* SLA cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <SlaCard
          title="Ultimo Full"
          icon={Archive}
          ageSeconds={data.sla.full_age_seconds}
          ok={data.sla.full_within_sla}
          slaTargetSeconds={data.sla.full_sla_target_seconds}
        />
        <SlaCard
          title="Ultimo Incr/Diff"
          icon={Database}
          ageSeconds={data.sla.incremental_age_seconds}
          ok={data.sla.incremental_within_sla}
          slaTargetSeconds={data.sla.incremental_sla_target_seconds}
        />
        <SlaCard
          title="WAL archiving"
          icon={Cloud}
          ageSeconds={data.sla.wal_last_archived_age_seconds}
          ok={data.sla.wal_archiving_active}
        />
        <Card className={data.pitr.window_seconds ? 'border-emerald-300 bg-emerald-500/5' : 'border-slate-300'}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Finestra PITR
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
              {data.pitr.window_seconds ? `${Math.round((data.pitr.window_seconds / 3600) * 10) / 10}h` : '—'}
            </p>
            <p className="text-xs text-muted-foreground">
              {data.pitr.earliest_recoverable_at
                ? <>da {new Date(data.pitr.earliest_recoverable_at).toLocaleString()}</>
                : 'nessuna finestra'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Inventario backup pgBackRest */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Archive className="h-4 w-4 text-purple-500" />
            Inventario backup ({backups.length})
          </CardTitle>
          {data.stanza?.pg_version && (
            <CardDescription>PostgreSQL {data.stanza.pg_version}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nessun backup registrato nella stanza</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Inizio</TableHead>
                  <TableHead>Età</TableHead>
                  <TableHead className="text-right">DB</TableHead>
                  <TableHead className="text-right">Backup</TableHead>
                  <TableHead className="text-right">Repo</TableHead>
                  <TableHead>Reference</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {backups.map((b: PgBackrestBackup) => {
                  const Icon = TYPE_ICON[b.type];
                  return (
                    <TableRow key={b.label} className={b.errored ? 'bg-rose-500/5' : undefined}>
                      <TableCell className="font-mono text-xs truncate max-w-xs">
                        {b.errored && <XCircle className="h-3 w-3 text-rose-600 inline mr-1" />}
                        {b.label}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs font-medium ${TYPE_BADGE[b.type]}`}>
                          <Icon className="h-3 w-3 mr-1" />
                          {TYPE_LABEL[b.type]}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{new Date(b.started_at).toLocaleString()}</TableCell>
                      <TableCell className="text-xs">{formatAge(b.age_seconds)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{formatBytes(b.size.database_bytes)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{formatBytes(b.size.backup_bytes)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{formatBytes(b.size.repo_bytes)}</TableCell>
                      <TableCell className="font-mono text-xs truncate max-w-[12rem]">
                        {b.reference.length > 0 ? b.reference.join(', ') : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Oggetti R2 reali */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Cloud className="h-4 w-4 text-cyan-500" />
            Oggetti repo R2 ({data.r2_objects.length})
          </CardTitle>
          <CardDescription>Listing reale del repository pgBackRest</CardDescription>
        </CardHeader>
        <CardContent>
          <R2Table objs={data.r2_objects.slice(0, 50)} />
        </CardContent>
      </Card>

      {/* Trigger assistito dialog */}
      <Dialog open={trigger.open} onOpenChange={(o) => { if (!trigger.busy) setTrigger(o ? { ...trigger, open: true } : INITIAL_TRIGGER); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Backup assistito</DialogTitle>
            <DialogDescription>
              L'esecuzione NON avviene dall'interfaccia. Seleziona il tipo e copia il comando da eseguire manualmente sul nodo DB-1.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-3 gap-2">
              {(['full', 'diff', 'incr'] as PgBackrestBackupType[]).map((t) => {
                const Icon = TYPE_ICON[t];
                return (
                  <Button
                    key={t}
                    variant={trigger.type === t ? 'default' : 'outline'}
                    className="justify-start"
                    onClick={() => void requestCommand(t)}
                    disabled={trigger.busy}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {TYPE_LABEL[t]}
                  </Button>
                );
              })}
            </div>
            {trigger.busy && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Generazione comando…
              </div>
            )}
            {trigger.command && !trigger.busy && (
              <div className="space-y-2">
                <div className="rounded-md border bg-muted/40 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <code className="font-mono text-xs break-all">{trigger.command.command}</code>
                    <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => void copyCommand()}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Nodo: <span className="font-mono">{trigger.command.node}</span> · {trigger.command.note}
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrigger(INITIAL_TRIGGER)} disabled={trigger.busy}>
              Chiudi
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function R2Table({ objs }: { objs: R2RepoObject[] }) {
  if (objs.length === 0) {
    return <p className="text-xs text-muted-foreground/70 italic">Nessun oggetto nel repo (collector R2 listing assente o vuoto)</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Key</TableHead>
          <TableHead className="text-right">Size</TableHead>
          <TableHead>Last modified</TableHead>
          <TableHead>Età</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {objs.map((o) => (
          <TableRow key={o.key}>
            <TableCell className="font-mono text-xs truncate max-w-lg">{o.key}</TableCell>
            <TableCell className="text-right font-mono text-xs">{formatBytes(o.size_bytes)}</TableCell>
            <TableCell className="font-mono text-xs">{new Date(o.last_modified).toLocaleString()}</TableCell>
            <TableCell className="text-xs">{formatAge(o.age_seconds)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
